import { Book, Thermometer, Moon, Brain, Baby, ChevronRight } from "lucide-react";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";

const examples = [
  {
    icon: Thermometer,
    title: "Fieber bei Kindern",
    description: "Eltern finden hier sofort sichere Lösungen",
    details: [
      "5 Naturmittel mit exakter Dosierung nach Alter",
      "Wadenwickel: Wann & wie (mit Temperaturangaben)",
      "Holunderblütentee: Dosierung für 2-6 Jahre vs. 6-12 Jahre",
      "⚠️ Wann zum Arzt: Fieber über 39°C länger als 3 Tage"
    ],
    page: "Seite 45-52"
  },
  {
    icon: Moon,
    title: "Schlafprobleme",
    description: "Endlich durchschlafen ohne Chemie",
    details: [
      "Melatonin: Richtige Dosierung (0,5-3mg) + Einnahmezeit",
      "Baldrian: Warum 2-4 Wochen nötig sind",
      "Lavendel: Anwendung als Öl, Tee oder Kapsel",
      "❌ Was NICHT funktioniert (und warum)"
    ],
    page: "Seite 112-125"
  },
  {
    icon: Brain,
    title: "Kopfschmerzen & Migräne",
    description: "Unterscheidung & gezielte Behandlung",
    details: [
      "Spannungskopfschmerz vs. Migräne erkennen",
      "Magnesium: Welche Form, welche Dosis (400mg/Tag)",
      "Pfefferminzöl: Anwendung auf Schläfen",
      "Mutterkraut: Prophylaxe bei häufiger Migräne"
    ],
    page: "Seite 78-91"
  },
  {
    icon: Baby,
    title: "Schwangerschaft",
    description: "Was ist erlaubt? Was verboten?",
    details: [
      "✅ Sichere Mittel: Ingwer gegen Übelkeit (max. 1g/Tag)",
      "✅ Himbeerblättertee ab 34. Woche",
      "❌ Verboten: Johanniskraut, hohe Vitamin-A-Dosen",
      "Eisenmangel: Natürliche Quellen + Dosierung"
    ],
    page: "Seite 230-258"
  }
];

const ProductContentSection = () => {
  const { ref, isVisible } = useScrollAnimation();

  return (
    <section className="py-20 bg-background">
      <div className="container mx-auto px-4">
        <div 
          ref={ref}
          className={`max-w-6xl mx-auto transition-all duration-700 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full mb-4">
              <Book className="w-5 h-5 text-primary" />
              <span className="text-sm font-semibold text-primary">Das steckt im E-Book</span>
            </div>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-foreground mb-4">
              Konkrete Beispiele aus dem Buch
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Keine leeren Versprechen – hier siehst du genau, was du für dein Geld bekommst
            </p>
          </div>

          {/* Examples Grid */}
          <div className="grid md:grid-cols-2 gap-6 mb-10">
            {examples.map((example, index) => {
              const Icon = example.icon;
              return (
                <div
                  key={index}
                  className="p-6 rounded-2xl bg-card border border-border hover:border-primary/30 shadow-soft hover:shadow-hover transition-all duration-300 group"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  {/* Header */}
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                      <Icon className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-serif text-xl font-semibold text-foreground mb-1">
                        {example.title}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {example.description}
                      </p>
                    </div>
                  </div>

                  {/* Details */}
                  <ul className="space-y-2 mb-4">
                    {example.details.map((detail, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-foreground/90">
                        <ChevronRight className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                        <span>{detail}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Page Reference */}
                  <div className="pt-3 border-t border-border/50">
                    <span className="text-xs font-medium text-primary/80 bg-primary/5 px-3 py-1 rounded-full">
                      📖 {example.page}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Bottom Note */}
          <div className="text-center p-6 rounded-2xl bg-gradient-to-br from-primary/5 to-accent/5 border border-primary/20">
            <p className="text-lg font-semibold text-foreground mb-2">
              Das sind nur 4 von über 50 Themen im E-Book
            </p>
            <p className="text-muted-foreground">
              640 Seiten • 150+ Naturmittel • Alle mit exakten Dosierungen
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductContentSection;
